import type { LocationItem, RideHistoryRow } from '@/models/rideOrdering';
import { useAddress } from '@/use/address';

const { getDistance, getFormattedAddress, getAddressObjectFromString } = useAddress();

const RECENT_LOCATION_RADIUS_MILES = 0.1;

const getDistanceMiles = (first: LocationItem, second: LocationItem): number | null => {
  if (first.latitude == null || first.longitude == null || second.latitude == null || second.longitude == null) {
    return null;
  }

  return getDistance(first.latitude, first.longitude, second.latitude, second.longitude);
};

/** Matches by address or within radius {@link RECENT_LOCATION_RADIUS_MILES} */
const locationsMatch = (first: LocationItem, second: LocationItem): boolean => {
  const firstAddress = getFormattedAddress({
    streetAddress: first.streetAddress || '',
    city: first.city || '',
    state: first.state || '',
    zip: first.zip || '',
    country: first.country,
    streetAddress2: first.streetAddress2,
  });
  const secondAddress = getFormattedAddress({
    streetAddress: second.streetAddress || '',
    city: second.city || '',
    state: second.state || '',
    zip: second.zip || '',
    country: second.country,
    streetAddress2: second.streetAddress2,
  });

  if (firstAddress && secondAddress && firstAddress === secondAddress) {
    return true;
  }

  const distance = getDistanceMiles(first, second);
  return distance != null && distance <= RECENT_LOCATION_RADIUS_MILES;
};

export const getMatchingLocation = (location: LocationItem, candidates: LocationItem[]): LocationItem | null => {
  return candidates.find((candidate) => locationsMatch(location, candidate)) ?? null;
};

export const getMatchingLocations = (location: LocationItem, candidates: LocationItem[]): LocationItem[] => {
  return candidates.filter((candidate) => locationsMatch(location, candidate));
};

const mapRideHistoryToLocation = (ride: RideHistoryRow, category: 'recent' | 'cancelled'): LocationItem | null => {
  if (ride.startLat == null || ride.startLong == null || !ride.startLocation) {
    return null;
  }

  return {
    id: `${category}-${ride.id}`,
    name: ride.startName || ride.startLocation,
    ...getAddressObjectFromString(ride.startLocation),
    latitude: Number(ride.startLat),
    longitude: Number(ride.startLong),
    category: 'recent',
    source: 'recent',
    occurredAt: ride.createdAt,
  };
};

export const getRecentLocations = (rides: RideHistoryRow[], category: 'recent' | 'cancelled'): LocationItem[] => {
  const previousLocations = rides
    .sort((first, second) => new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime())
    .map((ride) => mapRideHistoryToLocation(ride, category))
    .filter((location): location is LocationItem => location !== null);

  return previousLocations;
};

export const getSavedAddressIdentifier = (location: LocationItem): string | undefined => {
  if (location.savedAddressIdentifier) {
    return location.savedAddressIdentifier;
  }

  const identifiers: Record<string, string> = {
    home: 'home',
    aLocation: 'aName',
    bLocation: 'bName',
    cLocation: 'cName',
  };

  return identifiers[location.id];
};
